import { useState, useCallback, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MapContainer,
  TileLayer,
  Polygon,
  Popup,
  LayersControl,
  FeatureGroup,
  Marker,
  Polyline,
  useMapEvents,
} from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import L from 'leaflet';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  MapIcon,
  ChartBarIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  GlobeAltIcon,
  Square3Stack3DIcon,
  ScaleIcon,
  ArrowsPointingOutIcon,
} from '@heroicons/react/24/outline';
import { format, subDays } from 'date-fns';
import toast from 'react-hot-toast';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

// Fix Leaflet default marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface Farm {
  id: string;
  name: string;
  boundary: { lat: number; lon: number }[];
  areaHectares: number;
  cropType: string | null;
}

interface FieldStats {
  farmId: string;
  area: { squareMeters: number; hectares: number; acres: number };
  perimeter: { meters: number; kilometers: number };
  centroid: { lat: number; lon: number };
  boundingBox: { minLon: number; minLat: number; maxLon: number; maxLat: number };
  numPoints: number;
}

const TABS = [
  { id: 'field', label: 'Field Analysis', icon: MapIcon },
  { id: 'compare', label: 'Compare', icon: ChartBarIcon },
  { id: 'timeseries', label: 'Time Series', icon: ClockIcon },
  { id: 'import-export', label: 'Import/Export', icon: ArrowDownTrayIcon },
] as const;

type TabId = typeof TABS[number]['id'];

const INDEX_OPTIONS = [
  { value: 'ndvi', label: 'NDVI' },
  { value: 'evi', label: 'EVI' },
  { value: 'ndwi', label: 'NDWI' },
  { value: 'ndmi', label: 'NDMI' },
  { value: 'savi', label: 'SAVI' },
  { value: 'gndvi', label: 'GNDVI' },
  { value: 'lai', label: 'LAI' },
];

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316'];

// Measurement helper
function MeasurementOverlay({
  mode,
  points,
  onAddPoint,
}: {
  mode: 'distance' | 'area' | null;
  points: [number, number][];
  onAddPoint: (latlng: [number, number]) => void;
}) {
  useMapEvents({
    click(e) {
      if (mode) {
        onAddPoint([e.latlng.lat, e.latlng.lng]);
      }
    },
  });

  if (!mode || points.length === 0) return null;

  return (
    <>
      {points.map((p, i) => (
        <Marker key={i} position={p} />
      ))}
      {points.length > 1 && (
        <Polyline
          positions={mode === 'area' ? [...points, points[0]] : points}
          pathOptions={{ color: '#ef4444', weight: 2, dashArray: '6,6' }}
        />
      )}
    </>
  );
}

function calculateDistance(points: [number, number][]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const p1 = L.latLng(points[i - 1]);
    const p2 = L.latLng(points[i]);
    total += p1.distanceTo(p2);
  }
  return total;
}

function computeSphericalArea(points: L.LatLng[]): number {
  // Approximate area via Shoelace on projected coords
  if (points.length < 3) return 0;
  let area = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const xi = points[i].lng * Math.cos(points[i].lat * Math.PI / 180) * 111320;
    const yi = points[i].lat * 110540;
    const xj = points[j].lng * Math.cos(points[j].lat * Math.PI / 180) * 111320;
    const yj = points[j].lat * 110540;
    area += xi * yj - xj * yi;
  }
  return Math.abs(area) / 2;
}

export default function GeospatialTools() {
  const [activeTab, setActiveTab] = useState<TabId>('field');
  const [panelOpen, setPanelOpen] = useState(true);
  const [selectedFarmId, setSelectedFarmId] = useState<string | null>(null);
  const [selectedFarmIds, setSelectedFarmIds] = useState<string[]>([]);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [showLabels, setShowLabels] = useState(true);

  // Measurement state
  const [measureMode, setMeasureMode] = useState<'distance' | 'area' | null>(null);
  const [measurePoints, setMeasurePoints] = useState<[number, number][]>([]);

  // Field analysis state
  const [compareIndexType, setCompareIndexType] = useState('ndvi');
  const [compareDateStart, setCompareDateStart] = useState(
    subDays(new Date(), 90).toISOString().split('T')[0]
  );
  const [compareDateEnd, setCompareDateEnd] = useState(
    new Date().toISOString().split('T')[0]
  );

  // Time series state
  const [tsIndexType, setTsIndexType] = useState('ndvi');
  const [tsFarmId, setTsFarmId] = useState<string | null>(null);
  const [tsMonths, setTsMonths] = useState(6);

  // Import state
  const [importPreview, setImportPreview] = useState<unknown>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch farms
  const { data: farms = [], isLoading: farmsLoading } = useQuery({
    queryKey: ['farms'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/farms`, { headers: authHeaders() });
      return res.json() as Promise<Farm[]>;
    },
  });

  // Field stats query
  const { data: fieldStats, isLoading: statsLoading } = useQuery({
    queryKey: ['geospatial-field-stats', selectedFarmId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/geospatial/field/${selectedFarmId}/stats`, {
        headers: authHeaders(),
      });
      if (!res.ok) return null;
      return res.json() as Promise<FieldStats>;
    },
    enabled: !!selectedFarmId && activeTab === 'field',
  });

  // Elevation query
  const { data: elevationData } = useQuery({
    queryKey: ['geospatial-elevation', selectedFarmId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/geospatial/field/${selectedFarmId}/elevation`, {
        headers: authHeaders(),
      });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!selectedFarmId && activeTab === 'field',
  });

  // Slope query
  const { data: slopeData } = useQuery({
    queryKey: ['geospatial-slope', selectedFarmId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/geospatial/field/${selectedFarmId}/slope`, {
        headers: authHeaders(),
      });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!selectedFarmId && activeTab === 'field',
  });

  // Aspect query
  const { data: aspectData } = useQuery({
    queryKey: ['geospatial-aspect', selectedFarmId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/geospatial/field/${selectedFarmId}/aspect`, {
        headers: authHeaders(),
      });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!selectedFarmId && activeTab === 'field',
  });

  // Compare query
  const { data: compareData, isLoading: compareLoading } = useQuery({
    queryKey: ['geospatial-compare', selectedFarmIds, compareIndexType, compareDateStart, compareDateEnd],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/geospatial/compare`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          farmIds: selectedFarmIds,
          indexType: compareIndexType,
          dateRange: { startDate: compareDateStart, endDate: compareDateEnd },
        }),
      });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: selectedFarmIds.length > 0 && activeTab === 'compare',
  });

  // Ranking query
  const { data: rankingData } = useQuery({
    queryKey: ['geospatial-rank', compareIndexType],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/geospatial/rank?metric=${compareIndexType}`, {
        headers: authHeaders(),
      });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: activeTab === 'compare',
  });

  // Time series query
  const effectiveTsFarmId = tsFarmId || selectedFarmId;
  const { data: timeSeriesData, isLoading: tsLoading } = useQuery({
    queryKey: ['geospatial-timeseries', effectiveTsFarmId, tsIndexType, tsMonths],
    queryFn: async () => {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = subDays(new Date(), tsMonths * 30).toISOString().split('T')[0];
      const res = await fetch(
        `${API_URL}/geospatial/timeseries/${effectiveTsFarmId}?indexType=${tsIndexType}&startDate=${startDate}&endDate=${endDate}`,
        { headers: authHeaders() }
      );
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!effectiveTsFarmId && activeTab === 'timeseries',
  });

  // Trend query
  const { data: trendData } = useQuery({
    queryKey: ['geospatial-trend', effectiveTsFarmId, tsIndexType, tsMonths],
    queryFn: async () => {
      const res = await fetch(
        `${API_URL}/geospatial/trend/${effectiveTsFarmId}?indexType=${tsIndexType}&months=${tsMonths}`,
        { headers: authHeaders() }
      );
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!effectiveTsFarmId && activeTab === 'timeseries',
  });

  const handleFarmClick = useCallback(
    (farmId: string) => {
      if (multiSelectMode) {
        setSelectedFarmIds(prev =>
          prev.includes(farmId) ? prev.filter(id => id !== farmId) : [...prev, farmId]
        );
      } else {
        setSelectedFarmId(farmId);
        if (!tsFarmId) setTsFarmId(farmId);
      }
    },
    [multiSelectMode, tsFarmId]
  );

  const handleMeasurePoint = useCallback(
    (latlng: [number, number]) => {
      setMeasurePoints(prev => [...prev, latlng]);
    },
    []
  );

  const resetMeasurement = () => {
    setMeasureMode(null);
    setMeasurePoints([]);
  };

  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    const isKml = file.name.toLowerCase().endsWith('.kml');

    try {
      if (isKml) {
        const res = await fetch(`${API_URL}/geospatial/import/kml`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ kml: text }),
        });
        const data = await res.json();
        if (data.success) {
          toast.success(`Imported ${data.imported} farm(s) from KML`);
        } else {
          toast.error(data.errors?.[0] || 'Import failed');
        }
      } else {
        const geojson = JSON.parse(text);
        setImportPreview(geojson);
        toast.success('GeoJSON loaded. Click "Confirm Import" to save.');
      }
    } catch {
      toast.error('Failed to parse file');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const confirmGeoJSONImport = async () => {
    if (!importPreview) return;
    try {
      const res = await fetch(`${API_URL}/geospatial/import/geojson`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ geojson: importPreview }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Imported ${data.imported} farm(s)`);
        setImportPreview(null);
      } else {
        toast.error(data.errors?.[0] || 'Import failed');
      }
    } catch {
      toast.error('Import failed');
    }
  };

  const handleExport = async (fmt: 'geojson' | 'kml') => {
    const ids = selectedFarmIds.length > 0 ? selectedFarmIds : farms.map(f => f.id);
    if (ids.length === 0) {
      toast.error('No farms to export');
      return;
    }

    const idsParam = ids.join(',');
    try {
      const res = await fetch(`${API_URL}/geospatial/export/${fmt}?farmIds=${idsParam}`, {
        headers: authHeaders(),
      });

      if (!res.ok) throw new Error('Export failed');

      const blob = fmt === 'kml' ? new Blob([await res.text()], { type: 'application/vnd.google-earth.kml+xml' })
                                  : new Blob([JSON.stringify(await res.json(), null, 2)], { type: 'application/json' });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kenagri-farms.${fmt === 'kml' ? 'kml' : 'geojson'}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${ids.length} farm(s) as ${fmt.toUpperCase()}`);
    } catch {
      toast.error('Export failed');
    }
  };

  // Map center: Kenya default or first farm
  const mapCenter = useMemo<[number, number]>(() => {
    if (farms.length > 0 && farms[0].boundary?.length > 0) {
      const lats = farms[0].boundary.map(c => c.lat);
      const lons = farms[0].boundary.map(c => c.lon);
      return [
        (Math.min(...lats) + Math.max(...lats)) / 2,
        (Math.min(...lons) + Math.max(...lons)) / 2,
      ];
    }
    return [0.0236, 37.9062]; // Kenya center
  }, [farms]);

  // Measurement results
  const measureDistance = measurePoints.length >= 2 ? calculateDistance(measurePoints) : 0;
  const measureArea = measureMode === 'area' && measurePoints.length >= 3
    ? computeSphericalArea(measurePoints.map(p => L.latLng(p)))
    : 0;

  if (farmsLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] relative">
      {/* Map Area */}
      <div className={`flex-1 relative transition-all duration-300 ${panelOpen ? 'mr-0' : ''}`}>
        {/* Top bar */}
        <div className="absolute top-3 left-3 z-[1000] flex items-center space-x-2">
          <div className="bg-white rounded-lg shadow-lg px-3 py-2 flex items-center space-x-3">
            <span className="text-sm font-medium">{farms.length} Farms</span>
            <div className="h-4 w-px bg-gray-300" />
            <label className="flex items-center space-x-1.5 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={showLabels}
                onChange={(e) => setShowLabels(e.target.checked)}
                className="rounded text-green-600"
              />
              <span>Labels</span>
            </label>
            <div className="h-4 w-px bg-gray-300" />
            <label className="flex items-center space-x-1.5 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={multiSelectMode}
                onChange={(e) => setMultiSelectMode(e.target.checked)}
                className="rounded text-blue-600"
              />
              <span>Multi-select</span>
            </label>
          </div>
        </div>

        {/* Measurement toolbar */}
        <div className="absolute top-3 right-3 z-[1000]">
          <div className="bg-white rounded-lg shadow-lg p-2 space-y-1">
            <button
              onClick={() => { resetMeasurement(); setMeasureMode(measureMode === 'distance' ? null : 'distance'); }}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded text-sm w-full ${
                measureMode === 'distance' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
              }`}
              title="Measure distance"
            >
              <ScaleIcon className="h-4 w-4" />
              <span>Distance</span>
            </button>
            <button
              onClick={() => { resetMeasurement(); setMeasureMode(measureMode === 'area' ? null : 'area'); }}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded text-sm w-full ${
                measureMode === 'area' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
              }`}
              title="Measure area"
            >
              <ArrowsPointingOutIcon className="h-4 w-4" />
              <span>Area</span>
            </button>
            {measureMode && (
              <button
                onClick={resetMeasurement}
                className="flex items-center space-x-2 px-3 py-1.5 rounded text-sm w-full text-red-600 hover:bg-red-50"
              >
                <MinusIcon className="h-4 w-4" />
                <span>Clear</span>
              </button>
            )}
          </div>

          {/* Measurement result */}
          {measureMode && measurePoints.length >= 2 && (
            <div className="bg-white rounded-lg shadow-lg p-3 mt-2 text-sm">
              {measureMode === 'distance' && (
                <div>
                  <span className="font-medium">Distance: </span>
                  {measureDistance < 1000
                    ? `${Math.round(measureDistance)} m`
                    : `${(measureDistance / 1000).toFixed(2)} km`}
                </div>
              )}
              {measureMode === 'area' && measurePoints.length >= 3 && (
                <div>
                  <span className="font-medium">Area: </span>
                  {measureArea < 10000
                    ? `${Math.round(measureArea)} m\u00B2`
                    : `${(measureArea / 10000).toFixed(2)} ha`}
                </div>
              )}
              <div className="text-xs text-gray-500 mt-1">{measurePoints.length} points</div>
            </div>
          )}
        </div>

        {/* Panel toggle */}
        <button
          onClick={() => setPanelOpen(!panelOpen)}
          className="absolute top-1/2 -translate-y-1/2 right-0 z-[1000] bg-white shadow-lg rounded-l-lg p-1.5"
          style={{ right: panelOpen ? 'calc(40% - 1px)' : 0 }}
        >
          {panelOpen ? <ChevronRightIcon className="h-5 w-5" /> : <ChevronLeftIcon className="h-5 w-5" />}
        </button>

        {/* Map */}
        <MapContainer center={mapCenter} zoom={12} className="h-full w-full">
          <LayersControl position="topleft">
            <LayersControl.BaseLayer checked name="Satellite">
              <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Terrain">
              <TileLayer url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png" />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Street">
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            </LayersControl.BaseLayer>
          </LayersControl>

          <FeatureGroup>
            <EditControl
              position="topleft"
              draw={{
                rectangle: false,
                circle: false,
                circlemarker: false,
                marker: false,
                polyline: false,
                polygon: {
                  shapeOptions: { color: '#3b82f6', weight: 3, fillOpacity: 0.3 },
                },
              }}
            />
          </FeatureGroup>

          {/* Farm polygons */}
          {farms.map((farm) => {
            if (!farm.boundary || farm.boundary.length === 0) return null;
            const positions = farm.boundary.map(c => [c.lat, c.lon] as [number, number]);
            const isSelected = farm.id === selectedFarmId;
            const isMultiSelected = selectedFarmIds.includes(farm.id);
            const colorIdx = selectedFarmIds.indexOf(farm.id);

            return (
              <Polygon
                key={farm.id}
                positions={positions}
                pathOptions={{
                  color: isMultiSelected
                    ? COLORS[colorIdx % COLORS.length]
                    : isSelected
                    ? '#f59e0b'
                    : '#1e40af',
                  weight: isSelected || isMultiSelected ? 3 : 2,
                  fillColor: isMultiSelected
                    ? COLORS[colorIdx % COLORS.length]
                    : isSelected
                    ? '#f59e0b'
                    : '#3b82f6',
                  fillOpacity: isSelected || isMultiSelected ? 0.4 : 0.2,
                }}
                eventHandlers={{
                  click: () => handleFarmClick(farm.id),
                }}
              >
                <Popup>
                  <div className="min-w-[160px]">
                    <h3 className="font-bold text-sm">{farm.name}</h3>
                    <p className="text-xs text-gray-600">{farm.areaHectares?.toFixed(1)} ha</p>
                    <p className="text-xs text-gray-600">{farm.cropType || 'Unknown crop'}</p>
                    <button
                      onClick={() => { setSelectedFarmId(farm.id); setActiveTab('field'); setPanelOpen(true); }}
                      className="mt-2 px-2 py-1 bg-green-600 text-white rounded text-xs"
                    >
                      Analyze
                    </button>
                  </div>
                </Popup>
              </Polygon>
            );
          })}

          {/* Farm labels */}
          {showLabels &&
            farms.map((farm) => {
              if (!farm.boundary || farm.boundary.length === 0) return null;
              const lats = farm.boundary.map(c => c.lat);
              const lons = farm.boundary.map(c => c.lon);
              const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
              const centerLon = (Math.min(...lons) + Math.max(...lons)) / 2;

              return (
                <Marker
                  key={`label-${farm.id}`}
                  position={[centerLat, centerLon]}
                  icon={L.divIcon({
                    className: 'farm-label',
                    html: `<div style="background:rgba(255,255,255,0.85);padding:1px 6px;border-radius:4px;font-size:11px;white-space:nowrap;font-weight:500;border:1px solid #ddd">${farm.name}</div>`,
                    iconSize: [0, 0],
                    iconAnchor: [0, 0],
                  })}
                  interactive={false}
                />
              );
            })}

          {/* Measurement overlay */}
          <MeasurementOverlay
            mode={measureMode}
            points={measurePoints}
            onAddPoint={handleMeasurePoint}
          />
        </MapContainer>
      </div>

      {/* Side Panel */}
      {panelOpen && (
        <div className="w-[40%] min-w-[360px] max-w-[560px] bg-white border-l border-gray-200 flex flex-col overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-gray-200 bg-gray-50">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex flex-col items-center py-2.5 px-1 text-xs font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-green-600 text-green-700 bg-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="h-4 w-4 mb-1" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* ============ FIELD ANALYSIS TAB ============ */}
            {activeTab === 'field' && (
              <>
                {/* Farm selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Farm</label>
                  <select
                    value={selectedFarmId || ''}
                    onChange={(e) => setSelectedFarmId(e.target.value || null)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">-- Select a farm --</option>
                    {farms.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>

                {selectedFarmId && statsLoading && (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
                  </div>
                )}

                {fieldStats && (
                  <div className="space-y-4">
                    {/* Area & Perimeter */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-green-50 rounded-lg p-3">
                        <div className="text-xs text-green-600 font-medium">Area</div>
                        <div className="text-lg font-bold text-green-800">{fieldStats.area.hectares} ha</div>
                        <div className="text-xs text-green-600">{fieldStats.area.acres} acres</div>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-3">
                        <div className="text-xs text-blue-600 font-medium">Perimeter</div>
                        <div className="text-lg font-bold text-blue-800">{fieldStats.perimeter.kilometers} km</div>
                        <div className="text-xs text-blue-600">{fieldStats.perimeter.meters.toFixed(0)} m</div>
                      </div>
                    </div>

                    {/* Centroid */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-600 font-medium mb-1">Centroid</div>
                      <div className="text-sm font-mono">
                        {fieldStats.centroid.lat.toFixed(5)}, {fieldStats.centroid.lon.toFixed(5)}
                      </div>
                    </div>

                    {/* Elevation Profile */}
                    {elevationData?.profile && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Elevation Profile</h4>
                        <ResponsiveContainer width="100%" height={160}>
                          <LineChart data={elevationData.profile}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="distance"
                              tick={{ fontSize: 10 }}
                              label={{ value: 'km', position: 'insideBottom', offset: -2, fontSize: 10 }}
                            />
                            <YAxis
                              tick={{ fontSize: 10 }}
                              label={{ value: 'm', angle: -90, position: 'insideLeft', fontSize: 10 }}
                              domain={['auto', 'auto']}
                            />
                            <Tooltip
                              formatter={(v: number) => [`${v.toFixed(1)} m`, 'Elevation']}
                              labelFormatter={(l) => `${l} km`}
                            />
                            <Line
                              type="monotone"
                              dataKey="elevation"
                              stroke="#10b981"
                              strokeWidth={2}
                              dot={false}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>Min: {elevationData.summary.minElevation.toFixed(1)} m</span>
                          <span>Max: {elevationData.summary.maxElevation.toFixed(1)} m</span>
                          <span>Range: {elevationData.summary.elevationRange.toFixed(1)} m</span>
                        </div>
                      </div>
                    )}

                    {/* Slope */}
                    {slopeData && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Slope Analysis</h4>
                        <div className="flex items-center space-x-3 mb-2">
                          <div className={`px-2 py-1 rounded text-xs font-medium ${
                            slopeData.classification === 'flat' ? 'bg-green-100 text-green-700' :
                            slopeData.classification === 'gentle' ? 'bg-blue-100 text-blue-700' :
                            slopeData.classification === 'moderate' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {slopeData.classification.toUpperCase()}
                          </div>
                          <span className="text-sm">{slopeData.averageSlope.toFixed(1)} avg</span>
                          <span className="text-sm text-gray-500">{slopeData.maxSlope.toFixed(1)} max</span>
                        </div>
                        <p className="text-xs text-gray-600">{slopeData.description}</p>
                        <div className="mt-2 flex items-center space-x-2">
                          <span className="text-xs text-gray-500">Erosion risk:</span>
                          <span className={`text-xs font-medium ${
                            slopeData.erosionRisk === 'low' ? 'text-green-600' :
                            slopeData.erosionRisk === 'moderate' ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {slopeData.erosionRisk.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Aspect */}
                    {aspectData && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Field Orientation</h4>
                        <div className="flex items-center space-x-4">
                          {/* Compass graphic */}
                          <div className="relative w-16 h-16">
                            <div className="absolute inset-0 rounded-full border-2 border-gray-300" />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div
                                className="w-1 h-6 bg-red-500 rounded-full origin-bottom"
                                style={{
                                  transform: `rotate(${aspectData.azimuthDegrees}deg)`,
                                  transformOrigin: 'center bottom',
                                  position: 'absolute',
                                  bottom: '50%',
                                }}
                              />
                            </div>
                            <span className="absolute top-0 left-1/2 -translate-x-1/2 text-[9px] font-bold">N</span>
                            <span className="absolute bottom-0 left-1/2 -translate-x-1/2 text-[9px]">S</span>
                            <span className="absolute left-0 top-1/2 -translate-y-1/2 text-[9px]">W</span>
                            <span className="absolute right-0 top-1/2 -translate-y-1/2 text-[9px]">E</span>
                          </div>
                          <div>
                            <div className="text-lg font-bold">{aspectData.directionAbbr}</div>
                            <div className="text-sm text-gray-600">{aspectData.direction}-facing</div>
                            <div className="text-xs text-gray-500">{aspectData.azimuthDegrees.toFixed(0)}</div>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 mt-2">{aspectData.sunExposure}</p>
                      </div>
                    )}
                  </div>
                )}

                {selectedFarmId && !statsLoading && !fieldStats && (
                  <div className="text-center text-gray-500 py-8 text-sm">
                    No field data available. Make sure the database is connected.
                  </div>
                )}

                {!selectedFarmId && (
                  <div className="text-center text-gray-400 py-12">
                    <MapIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Click a farm on the map or select one above to view field analysis.</p>
                  </div>
                )}
              </>
            )}

            {/* ============ COMPARE TAB ============ */}
            {activeTab === 'compare' && (
              <>
                {/* Multi-farm selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Farms to Compare</label>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                    {farms.map((f) => (
                      <label key={f.id} className="flex items-center space-x-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={selectedFarmIds.includes(f.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedFarmIds(prev => [...prev, f.id]);
                            } else {
                              setSelectedFarmIds(prev => prev.filter(id => id !== f.id));
                            }
                          }}
                          className="rounded text-green-600"
                        />
                        <span>{f.name}</span>
                        <span className="text-xs text-gray-400 ml-auto">{f.areaHectares?.toFixed(1)} ha</span>
                      </label>
                    ))}
                  </div>
                  {selectedFarmIds.length > 0 && (
                    <div className="text-xs text-gray-500 mt-1">{selectedFarmIds.length} farm(s) selected</div>
                  )}
                </div>

                {/* Index & date selectors */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Index</label>
                    <select
                      value={compareIndexType}
                      onChange={(e) => setCompareIndexType(e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                    >
                      {INDEX_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                    <input
                      type="date"
                      value={compareDateStart}
                      onChange={(e) => setCompareDateStart(e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                    <input
                      type="date"
                      value={compareDateEnd}
                      onChange={(e) => setCompareDateEnd(e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                    />
                  </div>
                </div>

                {/* Comparison chart */}
                {compareLoading && (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
                  </div>
                )}

                {compareData?.results && compareData.results.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      {compareIndexType.toUpperCase()} Comparison
                    </h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={compareData.results}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="farmName"
                          tick={{ fontSize: 10 }}
                          interval={0}
                          angle={-20}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis tick={{ fontSize: 10 }} domain={[0, 'auto']} />
                        <Tooltip />
                        <Bar dataKey="avgValue" name={`Avg ${compareIndexType.toUpperCase()}`}>
                          {compareData.results.map((_: unknown, i: number) => (
                            <rect key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Rankings table */}
                {rankingData?.rankings && rankingData.rankings.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Farm Rankings</h4>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-gray-500 border-b">
                          <th className="text-left pb-2">#</th>
                          <th className="text-left pb-2">Farm</th>
                          <th className="text-right pb-2">Value</th>
                          <th className="text-right pb-2">Area (ha)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rankingData.rankings.map((r: any) => (
                          <tr key={r.farmId} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-1.5 font-medium">{r.rank}</td>
                            <td className="py-1.5 truncate max-w-[120px]">{r.farmName}</td>
                            <td className="py-1.5 text-right font-mono">{r.value ?? '-'}</td>
                            <td className="py-1.5 text-right text-gray-500">{r.areaHectares}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {selectedFarmIds.length === 0 && !rankingData && (
                  <div className="text-center text-gray-400 py-8">
                    <ChartBarIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Select farms above or enable multi-select on the map to compare.</p>
                  </div>
                )}
              </>
            )}

            {/* ============ TIME SERIES TAB ============ */}
            {activeTab === 'timeseries' && (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Farm</label>
                    <select
                      value={effectiveTsFarmId || ''}
                      onChange={(e) => setTsFarmId(e.target.value || null)}
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                    >
                      <option value="">-- Select --</option>
                      {farms.map((f) => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Index</label>
                    <select
                      value={tsIndexType}
                      onChange={(e) => setTsIndexType(e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                    >
                      {INDEX_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Months</label>
                    <select
                      value={tsMonths}
                      onChange={(e) => setTsMonths(parseInt(e.target.value))}
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                    >
                      <option value={3}>3 months</option>
                      <option value={6}>6 months</option>
                      <option value={12}>12 months</option>
                      <option value={24}>24 months</option>
                    </select>
                  </div>
                </div>

                {tsLoading && (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
                  </div>
                )}

                {/* Trend indicator */}
                {trendData && (
                  <div className="flex items-center space-x-3 bg-gray-50 rounded-lg p-3">
                    {trendData.trend === 'improving' && (
                      <ArrowTrendingUpIcon className="h-8 w-8 text-green-600" />
                    )}
                    {trendData.trend === 'declining' && (
                      <ArrowTrendingDownIcon className="h-8 w-8 text-red-600" />
                    )}
                    {(trendData.trend === 'stable' || trendData.trend === 'insufficient_data') && (
                      <MinusIcon className="h-8 w-8 text-gray-500" />
                    )}
                    <div>
                      <div className={`text-sm font-semibold ${
                        trendData.trend === 'improving' ? 'text-green-700' :
                        trendData.trend === 'declining' ? 'text-red-700' :
                        'text-gray-700'
                      }`}>
                        {trendData.trend.charAt(0).toUpperCase() + trendData.trend.slice(1).replace('_', ' ')}
                      </div>
                      {trendData.statistics && (
                        <div className="text-xs text-gray-500">
                          Mean: {trendData.statistics.mean} | Range: {trendData.statistics.min} - {trendData.statistics.max}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Time series chart */}
                {timeSeriesData?.data && timeSeriesData.data.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      {tsIndexType.toUpperCase()} Time Series
                    </h4>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={timeSeriesData.data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 9 }}
                          tickFormatter={(d) => {
                            try { return format(new Date(d), 'MMM d'); } catch { return d; }
                          }}
                        />
                        <YAxis tick={{ fontSize: 10 }} domain={[0, 'auto']} />
                        <Tooltip
                          labelFormatter={(d) => {
                            try { return format(new Date(d as string), 'MMM d, yyyy'); } catch { return d; }
                          }}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="meanValue"
                          name={`Mean ${tsIndexType.toUpperCase()}`}
                          stroke="#10b981"
                          strokeWidth={2}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="maxValue"
                          name="Max"
                          stroke="#3b82f6"
                          strokeWidth={1}
                          dot={false}
                          strokeDasharray="4 4"
                        />
                        <Line
                          type="monotone"
                          dataKey="minValue"
                          name="Min"
                          stroke="#f59e0b"
                          strokeWidth={1}
                          dot={false}
                          strokeDasharray="4 4"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                    <div className="text-xs text-gray-500 text-right mt-1">
                      {timeSeriesData.dataPoints} data points | Source: {timeSeriesData.source}
                    </div>
                  </div>
                )}

                {!effectiveTsFarmId && (
                  <div className="text-center text-gray-400 py-8">
                    <ClockIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Select a farm to view time series data.</p>
                  </div>
                )}
              </>
            )}

            {/* ============ IMPORT/EXPORT TAB ============ */}
            {activeTab === 'import-export' && (
              <>
                {/* Import */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Import Boundaries</h4>
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-green-400 hover:bg-green-50 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const file = e.dataTransfer.files[0];
                      if (file) {
                        const input = fileInputRef.current;
                        if (input) {
                          const dt = new DataTransfer();
                          dt.items.add(file);
                          input.files = dt.files;
                          input.dispatchEvent(new Event('change', { bubbles: true }));
                        }
                      }
                    }}
                  >
                    <ArrowUpTrayIcon className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Drag & drop GeoJSON or KML file</p>
                    <p className="text-xs text-gray-400 mt-1">or click to browse</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".geojson,.json,.kml"
                    onChange={handleFileImport}
                    className="hidden"
                  />
                </div>

                {/* Import preview */}
                {importPreview && (
                  <div className="bg-blue-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-blue-700">GeoJSON Preview</span>
                      <div className="space-x-2">
                        <button
                          onClick={confirmGeoJSONImport}
                          className="px-3 py-1 bg-green-600 text-white rounded text-xs"
                        >
                          Confirm Import
                        </button>
                        <button
                          onClick={() => setImportPreview(null)}
                          className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                    <pre className="text-xs text-gray-600 max-h-32 overflow-auto bg-white rounded p-2">
                      {JSON.stringify(importPreview, null, 2).substring(0, 500)}...
                    </pre>
                  </div>
                )}

                {/* Export */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Export Boundaries</h4>
                  <p className="text-xs text-gray-500 mb-3">
                    {selectedFarmIds.length > 0
                      ? `Export ${selectedFarmIds.length} selected farm(s)`
                      : `Export all ${farms.length} farm(s)`}
                  </p>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => handleExport('geojson')}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                    >
                      <GlobeAltIcon className="h-4 w-4" />
                      <span>GeoJSON</span>
                    </button>
                    <button
                      onClick={() => handleExport('kml')}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                    >
                      <Square3Stack3DIcon className="h-4 w-4" />
                      <span>KML</span>
                    </button>
                  </div>
                </div>

                {/* Format info */}
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <h4 className="text-xs font-semibold text-gray-600">Supported Formats</h4>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p><span className="font-medium">GeoJSON:</span> Standard geospatial format. Works with QGIS, ArcGIS, Mapbox.</p>
                    <p><span className="font-medium">KML:</span> Google Earth format. Works with Google Earth, Maps, GPS devices.</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
